import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { getBookingsForDate, getOpeningHours, getBlockedDates } from '../services/pocketbase';
import { calculateFreeSlots } from '../utils/availability';
import { FiClock, FiCalendar } from 'react-icons/fi';
import './BookingCalendar.css';

const BookingCalendar = ({ onSlotSelect, selectedServiceDuration = 60 }) => {
    const [date, setDate] = useState(new Date());
    const [availableSlots, setAvailableSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [openingHoursData, setOpeningHoursData] = useState([]);
    const [blockedDates, setBlockedDates] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);

    // Charger les données de configuration (Horaires + Jours fériés)
    useEffect(() => {
        const loadConfig = async () => {
            try {
                const hours = await getOpeningHours();
                setOpeningHoursData(hours);
            } catch (err) {
                console.error("Erreur horaires:", err);
            }

            try {
                const blocked = await getBlockedDates();
                setBlockedDates(blocked);
            } catch (err) {
                console.error("Erreur dates bloquées (peut-être collection manquante):", err);
            }
        };
        loadConfig();
    }, []);

    // Quand la date change, recalculer les slots
    useEffect(() => {
        if (!date) return;

        const fetchSlots = async () => {
            setLoadingSlots(true);
            setAvailableSlots([]);
            setSelectedSlot(null);

            try {
                // 1. Vérifier si la date est bloquée (Congés)
                const isBlocked = isDateBlocked(date);
                if (isBlocked) {
                    setAvailableSlots([]);
                    setLoadingSlots(false);
                    return;
                }

                // 2. Récupérer les bookings du jour
                const existingBookings = await getBookingsForDate(date);

                // 3. Trouver les horaires pour ce jour
                let dayIndex = date.getDay();
                if (dayIndex === 0) dayIndex = 7; // Dimanche = 7

                const todayHours = openingHoursData.find(h => h.day === dayIndex);

                // 4. Calculer les slots
                if (todayHours && !todayHours.is_closed) {
                    const slots = calculateFreeSlots(date, todayHours, existingBookings, selectedServiceDuration);
                    setAvailableSlots(slots);
                } else {
                    setAvailableSlots([]); // Fermé régulièrement
                }

            } catch (error) {
                console.error("Erreur calcul slots:", error);
            } finally {
                setLoadingSlots(false);
            }
        };

        fetchSlots();
        onSlotSelect({ date: date, time: null });

    }, [date, openingHoursData, blockedDates, selectedServiceDuration, onSlotSelect]);

    // Helper pour vérifier si une date est bloquée
    const isDateBlocked = (dateToCheck) => {
        return blockedDates.some(blocked => {
            // Comparaison simple par string YYYY-MM-DD
            const bDate = new Date(blocked.date);
            return bDate.toDateString() === dateToCheck.toDateString();
        });
    };

    // Helper pour vérifier si un jour est fermé (routine)
    const isDayClosedRoutine = (dateToCheck) => {
        if (openingHoursData.length === 0) return false;
        let dayIndex = dateToCheck.getDay();
        if (dayIndex === 0) dayIndex = 7;
        const todayHours = openingHoursData.find(h => h.day === dayIndex);
        return todayHours ? todayHours.is_closed : false;
    };

    const handleSlotClick = (time) => {
        setSelectedSlot(time);
        onSlotSelect({ date, time });
    };

    // Fonction pour désactiver les tuiles du calendrier (visuel)
    const tileDisabled = ({ date, view }) => {
        if (view === 'month') {
            // Bloquer si c'est un jour passé (avant aujourd'hui minuit)
            const yesterday = new Date();
            yesterday.setHours(0, 0, 0, 0);
            yesterday.setDate(yesterday.getDate() - 1);
            if (date < yesterday) return true;

            // Bloquer si c'est un congé
            if (isDateBlocked(date)) return true;

            // Bloquer si c'est un jour de fermeture hebdomadaire
            // (Optionnel : certains préfèrent laisser le jour cliquable pour voir "Fermé")
            // Decommenter la ligne suivante si vous voulez griser les lundis/dimanches fermés
            // if (isDayClosedRoutine(date)) return true; 
        }
        return false;
    };

    // Classe CSS conditionnelle pour les tuiles
    const tileClassName = ({ date, view }) => {
        if (view === 'month') {
            if (isDateBlocked(date)) return 'calendar-tile-blocked';
            if (isDayClosedRoutine(date)) return 'calendar-tile-closed';
        }
    };

    return (
        <div className="booking-calendar-container">
            <div className="calendar-section">
                <h4><FiCalendar /> Choisissez une date</h4>
                <Calendar
                    onChange={setDate}
                    value={date}
                    minDate={new Date()} // Pas de date passée (interraction)
                    view="month"
                    className="custom-calendar"
                    locale="fr-FR"
                    tileDisabled={tileDisabled}
                    tileClassName={tileClassName}
                />
                <div className="calendar-legend">
                    <span className="legend-item"><span className="dot blocked"></span> Congés / Fériés</span>
                    <span className="legend-item"><span className="dot closed"></span> Fermé</span>
                </div>
            </div>

            <div className="slots-section">
                <h4><FiClock /> Horaires disponibles</h4>
                <div className="slots-date-display">
                    {date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                </div>

                {loadingSlots ? (
                    <div className="slots-loader">Calcul des disponibilités...</div>
                ) : availableSlots.length > 0 ? (
                    <div className="slots-grid">
                        {availableSlots.map(time => (
                            <button
                                key={time}
                                className={`slot-btn ${selectedSlot === time ? 'active' : ''}`}
                                onClick={() => handleSlotClick(time)}
                            >
                                {time}
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="no-slots-message">
                        {isDateBlocked(date) ? (
                            <><span className="text-danger">Congés / Fermeture exceptionnelle.</span><br />Veuillez choisir une autre date.</>
                        ) : isDayClosedRoutine(date) ? (
                            "Le salon est fermé ce jour-là."
                        ) : (
                            "Aucun créneau disponible (Complet)."
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BookingCalendar;
